import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessHandlePage() {
  const { user, authHeader, refreshUser } = useAuth();
  const nav = useNavigate();
  const [handle, setHandle] = useState("");
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null); // { available, valid, reason? }
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { nav("/login?next=/business/handle"); return; }
    if (user.role !== "business") { nav("/"); return; }
    setHandle(user.business_handle || "");
    // Prefetch suggestions once we have the user
    if (user.business_name) {
      axios
        .get(`${API}/handles/suggest`, {
          headers: authHeader(),
          params: { business_name: user.business_name, city: user.city || "", state: user.state || "" },
        })
        .then((r) => setSuggestions(r.data?.suggestions || []))
        .catch(() => setSuggestions([]));
    }
  }, [user, nav, authHeader]);

  const checkAvailability = async (value) => {
    const v = value.trim().replace(/^@/, "").toLowerCase();
    if (!v) { setStatus(null); return; }
    if (v === user?.business_handle) { setStatus({ available: true, valid: true, current: true }); return; }
    setChecking(true);
    try {
      const r = await axios.get(`${API}/handles/check`, { headers: authHeader(), params: { handle: v } });
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setChecking(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase();
    setHandle(v);
    // Simple debounce via clearing/setting a timer on the DOM element
    if (window.__handleCheckT) clearTimeout(window.__handleCheckT);
    window.__handleCheckT = setTimeout(() => checkAvailability(v), 300);
  };

  const save = async () => {
    if (!status?.valid || !status?.available) return;
    setSaving(true);
    try {
      await axios.post(`${API}/business/handle`, { handle }, { headers: authHeader() });
      toast.success(`Your handle is now @${handle}`);
      if (refreshUser) await refreshUser();
      nav("/business");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update handle");
    } finally {
      setSaving(false);
    }
  };

  const applySuggestion = (s) => {
    setHandle(s);
    checkAvailability(s);
  };

  return (
    <div className="App">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Business</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: "var(--gf-text)" }}>
          Your BookFreely handle
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--gf-text-soft)" }}>
          Your handle is your public profile URL — e.g. <span style={{ color: "var(--gf-accent)" }}>bookfreely.co/business/@{handle || "yourhandle"}</span>.
          Only lowercase letters, numbers and underscores. You can change your handle once every 30 days.
        </p>

        <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
          <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Handle</label>
          <div className="mt-2 flex items-center gap-2">
            <span style={{ color: "var(--gf-text-muted)" }}>@</span>
            <input
              data-testid="handle-input"
              value={handle}
              onChange={onChange}
              maxLength={30}
              placeholder="squeakycleanpro"
              className="flex-1 bg-transparent border rounded-md px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            />
          </div>
          <div className="mt-2 min-h-[20px] text-xs" data-testid="handle-status">
            {checking && <span style={{ color: "var(--gf-text-muted)" }}>Checking…</span>}
            {!checking && status?.current && <span style={{ color: "var(--gf-text-muted)" }}>This is your current handle.</span>}
            {!checking && status && !status.current && status.valid && status.available && (
              <span style={{ color: "#059669" }}>✓ @{handle} is available</span>
            )}
            {!checking && status && !status.valid && (
              <span style={{ color: "#dc2626" }}>{status.reason || "This username can't be used."}</span>
            )}
            {!checking && status && status.valid && !status.available && !status.current && (
              <span style={{ color: "#dc2626" }}>@{handle} is already taken.</span>
            )}
          </div>
          <button
            data-testid="handle-save-btn"
            onClick={save}
            disabled={saving || !status?.valid || !status?.available || status?.current}
            className="mt-4 rounded-full px-5 py-2 text-sm font-medium disabled:opacity-50"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            {saving ? "Saving…" : "Save handle"}
          </button>
        </div>

        {suggestions.length > 0 && (
          <div className="mt-6" data-testid="handle-suggestions">
            <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Suggestions</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => applySuggestion(s)}
                  className="text-sm rounded-full border px-3 py-1.5 hover:opacity-80"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
                  data-testid={`handle-suggest-${s}`}
                >
                  @{s}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
